// Feature flags read from GET /api/health (WO Track B3 — FEATURE_COI deploys dark).
// The health endpoint is public and cheap; we cache it for ~5 minutes. While loading or on
// error the SAFE fallback is "COI disabled, mock engine" so the gated surface never flashes
// on for a deploy where the flag is off.
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api.js";
import { qk } from "../lib/queryKeys.js";

export interface Features {
  coi: { enabled: boolean; engine: "mock" | "prophy" };
  /** WO-7/30 Track B: star-rating criteria guide. "" ⇒ no guide published; show the hint only. */
  ratingsGuideUrl?: string;
  /** Staging/dev Data Ops (sync / snapshot / restore). Off unless DATA_OPS_ENABLED=1. */
  dataOps?: { enabled: boolean };
  /** Cartoon snapshot reseed from the login picker. Off on app-dev / staging Cloud SQL. */
  devSnapshotLoad?: { allowed: boolean };
  /** Google OAuth. On when the API has GOOGLE_CLIENT_ID (independent of AUTH_MODE). */
  google?: { enabled: boolean };
  /** Identity Platform email-link. Off unless EMAIL_LINK_AUTH=1 and Firebase config is set. */
  emailLink?: {
    enabled: boolean;
    openAccess: boolean;
    firebase: { apiKey: string; authDomain: string; projectId: string } | null;
  };
}

interface HealthResponse {
  ok: boolean;
  env: string;
  service: string;
  sha: string;
  nodeEnv: string;
  authMode: string;
  features: Features;
}

const SAFE_FALLBACK: Features = {
  coi: { enabled: false, engine: "mock" },
  ratingsGuideUrl: "",
  dataOps: { enabled: false },
  // Fail closed: never auto-reseed if health is unknown.
  devSnapshotLoad: { allowed: false },
  google: { enabled: false },
  emailLink: { enabled: false, openAccess: false, firebase: null },
};

/** Demo snapshot load is island/local only. Shared lanes never reseed from login. */
export function demoSnapshotLoadAllowed(features: Features, deployEnv?: string): boolean {
  if (features.devSnapshotLoad) return features.devSnapshotLoad.allowed;
  const env = (deployEnv ?? "").toLowerCase();
  return env !== "dev" && env !== "staging" && env !== "production" && env !== "prod";
}

export function useFeatures(): {
  features: Features;
  /** From /api/health; undefined while loading / on error. */
  authMode: "dev" | "real" | undefined;
  /** DEPLOY_ENV from /api/health (staging | sandbox | local | …). Used for MOTD channel. */
  deployEnv: string | undefined;
  isLoading: boolean;
  /** True when the health fetch FAILED (after retries) — `features` is then a guess, not a
   *  server answer. Gated surfaces should offer a retry instead of silently redirecting. */
  isError: boolean;
  refetch: () => void;
} {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: qk.features,
    queryFn: () => api.get<HealthResponse>("/health"),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
  const mode = data?.authMode;
  return {
    features: data?.features ?? SAFE_FALLBACK,
    authMode: mode === "dev" || mode === "real" ? mode : undefined,
    deployEnv: data?.env,
    isLoading,
    isError,
    refetch: () => void refetch(),
  };
}
